import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import SeedField from "@/components/SeedField";
import MaskedReveal from "@/components/MaskedReveal";
import { MEDIA } from "@/lib/media";
import { SEEDKEEPERS_URL } from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: i, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative h-screen min-h-[760px] w-full overflow-hidden bg-rainforest" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline poster={MEDIA.heroPoster}>
          <source src={MEDIA.heroVideo} type="video/mp4" />
        </video>
      </motion.div>

      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-gradient-to-b from-rainforest/70 via-charcoal/40 to-rainforest/90" />
      <SeedField count={18} />

      <motion.div style={{ y: textY }} className="relative h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-center">
        <div className="inline-block self-start bg-black/40 backdrop-blur-[3px] px-6 py-7 md:px-10 md:py-9 max-w-5xl">
          <motion.p custom={0.15} variants={fade} initial="hidden" animate="show" className="overline text-ochre mb-6">
            SeedKeepers Academy
          </motion.p>

          <h1 className="font-serif text-cream font-bold leading-[1.0] tracking-tight text-4xl sm:text-6xl lg:text-7xl">
            <MaskedReveal lines={["Australia Is Entering a", "Multi-Billion Dollar", "Restoration Decade"]} lineClassName="" start={0.3} stagger={0.12} />
          </h1>

          <motion.p custom={0.95} variants={fade} initial="hidden" animate="show" className="mt-8 max-w-2xl text-cream/85 text-base md:text-lg font-light leading-relaxed">
            Australia is entering a <span className="font-semibold text-cream">multi-billion dollar decade of investment</span> in ecological
            restoration, biodiversity recovery and climate resilience. SeedKeepers Academy develops
            the practical skills, ecological understanding and enterprise capability needed to lead
            in native seed collection, seed banking, propagation and restoration supply.
          </motion.p>
        </div>

        <motion.div custom={1.2} variants={fade} initial="hidden" animate="show" className="mt-11 flex flex-wrap gap-4">
          <Link to="/courses" data-testid="hero-explore-btn" className="group relative overflow-hidden bg-cream text-rainforest px-9 py-4 text-sm tracking-wide">
            <span className="relative z-10">Explore Courses</span>
            <span className="absolute inset-0 bg-ochre translate-y-full group-hover:translate-y-0 transition-transform duration-400" />
            <span className="absolute inset-0 z-10 flex items-center justify-center text-cream translate-y-full group-hover:translate-y-0 transition-transform duration-400">Explore Courses</span>
          </Link>
          <Link to="/on-country" data-testid="hero-oncountry-btn" className="border border-cream/50 text-cream px-9 py-4 text-sm tracking-wide hover:bg-cream hover:text-rainforest transition-colors">
            Discuss On-Country Training
          </Link>
          <Link to="/contact" data-testid="hero-join-btn" className="bg-ochre text-cream px-9 py-4 text-sm tracking-wide hover:bg-copper transition-colors">
            Join the Community
          </Link>
        </motion.div>

        <motion.a custom={1.35} variants={fade} initial="hidden" animate="show" href={SEEDKEEPERS_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-created-by" className="group mt-8 inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-wide transition-colors">
          <ArrowLeft size={12} className="transition-transform group-hover:-translate-x-1" />
          Created by SeedKeepers
        </motion.a>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-cream/60">
        <span className="overline text-[0.6rem]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-8 bg-cream/40" />
      </div>
    </section>
  );
}
